import SearchBar from '@components/SearchBar/SearchBar'
import TokenAvatar from '@components/TokenAvatar'
import { config } from '@plug-core/appConfig'
import ICPSwap from '@plug-core/clients/ICPSwap'
import useConfig from '@plug-core/hooks/useConfig'
import useDebouncedState from '@plug-core/hooks/useDebouncedState'
import useToken from '@plug-core/hooks/useToken'
import { SwapTokenPair } from '@plug-core/interfaces/swapInterface'
import { logger } from '@plug-core/logger'
import { ListGroup, ListRow } from '@ui'
import { ROUTES } from '@utils/routes'
import React, { FC, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

interface PairRowProps {
  pair: SwapTokenPair
}

const PairRow: FC<PairRowProps> = ({ pair }) => {
  const navigate = useNavigate()

  const { logo: token0Logo, symbol: token0Symbol } = useToken({ address: pair.token0.principal })
  const { logo: token1Logo, symbol: token1Symbol } = useToken({ address: pair.token1.principal })

  const navigateToFallback = () => {
    navigate(ROUTES.ICPSWAP_FALLBACK_PAIR, { state: { pair } })
  }

  return (
    <ListRow
      key={pair.swapCanisterPrincipal}
      onClick={navigateToFallback}
      leading={
        <span className="flex">
          <TokenAvatar logo={token0Logo} fallbackName={token0Symbol} />
          <span className="-ml-2 rounded-full ring-2 ring-surface">
            <TokenAvatar logo={token1Logo} fallbackName={token1Symbol} />
          </span>
        </span>
      }
      primary={`${token0Symbol}/${token1Symbol}`}
      chevron
    />
  )
}

const PairPicker = () => {
  const [pairs, setPairs] = useState<SwapTokenPair[]>([])
  const [debouncedSearchTerm, setSearchTerm, searchTerm] = useDebouncedState('', 300)

  const { state } = useLocation()
  const { tokens } = useConfig()

  const { t } = useTranslation()

  const dexClient = useMemo(() => {
    const agent = config.getAgent()

    return new ICPSwap(agent)
  }, [state])

  useEffect(() => {
    const supportedCanisters = tokens.map((token) => token.address)

    dexClient
      .getPairs()
      .then((pairs) => {
        setPairs(
          pairs.filter(
            (pair) =>
              supportedCanisters.includes(pair.token0.principal) &&
              supportedCanisters.includes(pair.token1.principal)
          )
        )
      })
      .catch((e) => logger.error('Failed to get swap fallback pairs', { originalError: e }))
  }, [dexClient, tokens])

  const filteredPairs = useMemo(() => {
    if (!debouncedSearchTerm) return pairs

    return pairs.filter((pair) => {
      const token0 = tokens.find((token) => token.address === pair.token0.principal)
      const token1 = tokens.find((token) => token.address === pair.token1.principal)

      return `${token0?.name?.toLowerCase()}/${token1?.name?.toLowerCase()}`.includes(
        debouncedSearchTerm.toLowerCase()
      )
    })
  }, [debouncedSearchTerm, tokens, pairs])

  return (
    <div className="mb-3 flex flex-col gap-4">
      <SearchBar
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        placeholder={t('swap.searchPair')}
      />
      {filteredPairs.length > 0 && (
        <ListGroup>
          {filteredPairs.map((pair, idx) => (
            <PairRow key={idx} pair={pair} />
          ))}
        </ListGroup>
      )}
    </div>
  )
}

export default PairPicker
